import { useState, useCallback, useRef, useEffect } from 'react';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';

const STORAGE_KEY = 'fishle-directory';

const loadLocalDirectory = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

const computeDirectoryUpdate = (existingEntry, fish) => {
  const catchWeight = parseFloat(fish.weight);
  const catchValue  = fish.sellPrice;
  const catchEffect = fish.effect;
  const catchRarity = fish.rarity;

  // First discovery
  if (!existingEntry) {
    return {
      discoveredAt:  new Date().toISOString(),
      maxWeight:     catchWeight,
      maxGoldValue:  catchValue,
      variantsSeen:  catchEffect !== 'default' ? [catchEffect] : [],
      raritiesSeen:  [catchRarity],
    };
  }

  // Existing entry
  const updatedEntry = { ...existingEntry };
  let changed = false;

  if (catchWeight > existingEntry.maxWeight) {
    updatedEntry.maxWeight = catchWeight;
    changed = true;
  }
  if (catchValue > existingEntry.maxGoldValue) {
    updatedEntry.maxGoldValue = catchValue;
    changed = true;
  }
  if (catchEffect !== 'default' && !existingEntry.variantsSeen.includes(catchEffect)) {
    updatedEntry.variantsSeen = [...existingEntry.variantsSeen, catchEffect];
    changed = true;
  }
  // Track rarity — guard against missing raritiesSeen on older entries
  const raritiesSeen = existingEntry.raritiesSeen ?? [];
  if (!raritiesSeen.includes(catchRarity)) {
    updatedEntry.raritiesSeen = [...raritiesSeen, catchRarity];
    changed = true;
  }

  return changed ? updatedEntry : null;
};

export const mergeDirectories = (local, cloud) => {
  const merged = { ...cloud };

  for (const [fishId, localEntry] of Object.entries(local)) {
    if (!merged[fishId]) {
      merged[fishId] = localEntry;
    } else {
      const cloudEntry = merged[fishId];
      merged[fishId] = {
        discoveredAt: localEntry.discoveredAt < cloudEntry.discoveredAt
          ? localEntry.discoveredAt
          : cloudEntry.discoveredAt,
        maxWeight:    Math.max(localEntry.maxWeight,    cloudEntry.maxWeight),
        maxGoldValue: Math.max(localEntry.maxGoldValue, cloudEntry.maxGoldValue),
        variantsSeen: [...new Set([
          ...localEntry.variantsSeen,
          ...cloudEntry.variantsSeen,
        ])],
        raritiesSeen: [...new Set([          // ← add this
          ...(localEntry.raritiesSeen ?? []),
          ...(cloudEntry.raritiesSeen ?? []),
        ])],
      };
    }
  }

  return merged;
};

export function useDirectory({ user } = {}) {

  const [directory, setDirectory] = useState(loadLocalDirectory);

  // Only userRef needed — directoryRef removed since setDirectory
  // functional updater always reads from committed state directly
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  /**
   * updateDirectory
   *
   * Uses functional setDirectory updater so rapid successive calls
   * (e.g. inventory backfill on login) each build on the previous
   * committed state rather than reading a shared stale ref.
   */
  const updateDirectory = useCallback((fish) => {
    if (!fish?.id) return;

    setDirectory(prev => {
      const existingEntry = prev[fish.id];
      const updatedEntry  = computeDirectoryUpdate(existingEntry, fish);

      if (!updatedEntry) return prev;

      const nextDirectory = { ...prev, [fish.id]: updatedEntry };

      // Persist inside updater — always reflects accumulated state
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextDirectory));

      // Firebase write
      const currentUser = userRef.current;
      if (currentUser?.uid) {
        updateDoc(doc(db, 'users', currentUser.uid), {
          [`fishDirectory.${fish.id}`]: updatedEntry,
        }).catch(error => {
          console.error('Error updating fish directory:', error);
        });
      }

      return nextDirectory;
    });
  }, []);

  const loadCloudDirectory = useCallback((cloudDirectory) => {
    if (!cloudDirectory) return;

    setDirectory(prev => {
      const merged = mergeDirectories(prev, cloudDirectory);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      return merged;
    });
  }, []);

  const resetDirectory = useCallback(() => {
    setDirectory({});
  }, []);

  return {
    directory,
    updateDirectory,
    loadCloudDirectory,
    resetDirectory,
  };
}